<template>
    <FormGroup>

        <!-- Text -->
        <template v-if="type === 'text'">
            <div class="input-group">
                <Input type="text" v-model="modelText[lang]" :placeholder="dynPlaceholder(lang)" :hasError="form.errors.has(name)" @input="input => inputModelText(input, lang)" :key="lang" :mask="mask" :focus="focus" />
                <template v-if="translate">
                    <button class="btn btn-light border-0 bg-light px-1 hover-none" :class="[ { 'pe-2' : index === modelAvailableLanguages.length - 1 }, (lng === lang ? 'text-primary' : 'text-secondary') ]" type="button" v-for="(lng, index) in modelAvailableLanguages" @click="setLang(lng)">{{ lng }}</button>
                </template>
            </div>
        </template>

        <!-- Password -->
        <Input v-else-if="type === 'password'" type="password" v-model="model" :placeholder="placeholder" :hasError="form.errors.has(name)" @input="form.errors.clear(name)" :focus="focus" :mask="mask" />

        <!-- Number -->
        <Input v-else-if="type === 'number'" type="number" v-model="model" :placeholder="placeholder" :hasError="form.errors.has(name)" @input="form.errors.clear(name)" :min="min" :max="max" :focus="focus" />

        <!-- Date -->
        <Input v-else-if="type === 'date'" type="date" v-model="modelDate" :placeholder="placeholder" :hasError="form.errors.has(name)" @input="form.errors.clear(name)" :min="min" :max="max" :focus="focus" />

        <!-- Date time -->
        <Input v-else-if="type === 'datetime'" type="datetime-local" step="1" v-model="modelDateTime" :placeholder="placeholder" :hasError="form.errors.has(name)" @input="form.errors.clear(name)" :min="min" :max="max" :focus="focus" />

        <!-- Phone -->
        <PhoneNumber v-else-if="type === 'phone'" v-model="model" :placeholder="placeholder" :hasError="form.errors.has(name)" @input="form.errors.clear(name)" />

        <!-- Textarea -->
        <template v-if="type === 'textarea'">
            <template v-if="translate"><a href="#" class="pe-1 text-xs mb-1 text-decoration-none" :class="lng === lang ? 'text-primary' : 'text-secondary'" type="button" v-for="lng in modelAvailableLanguages" @click.prevent="setLang(lng)">{{ lng }}</a></template>
            <Textarea v-model="modelText[lang]" :placeholder="dynPlaceholder(lang)" :hasError="form.errors.has(name)" @input="input => inputModelText(input, lang)" :key="lang" />
        </template>

        <!-- Editor -->
        <template v-if="type === 'editor'">
            <template v-if="translate"><a href="#" class="pe-1 text-xs mb-1 text-decoration-none" :class="lng === lang ? 'text-primary' : 'text-secondary'" type="button" v-for="lng in modelAvailableLanguages" @click.prevent="setLang(lng)">{{ lng }}</a></template>
            <Editor v-model="modelText[lang]" :placeholder="dynPlaceholder(lang)" :hasError="form.errors.has(name)" @input="input => inputModelText(input, lang)" :key="lang" />
        </template>

        <!-- Radio -->
        <template v-else-if="type === 'radio'">
            <Radio v-for="(option, index) in radioOptions" v-model="model" :key="index" :option="option" :hasError="form.errors.has(name)" @input="form.errors.clear(name)" />
        </template>

        <!-- Checkbox -->
        <template v-else-if="type === 'checkbox'">
            <Checkbox v-model="model" @input="form.errors.clear(name)"><span v-if="placeholder">{{ placeholder }}</span><slot v-else></slot></Checkbox>
        </template>

        <!-- Boolean -->
        <template v-else-if="type === 'boolean'">
            <Checkbox v-model="model" @input="form.errors.clear(name)"><span v-if="placeholder">{{ placeholder }}</span><slot v-else></slot></Checkbox>
        </template>

        <!-- Select -->
        <Select v-else-if="type === 'select'" v-model="model" :options="options" :placeholder="placeholder" :trackBy="optionsKeyBy" :label="optionsLabel" :multiple="multiple" :hasError="form.errors.has(name)" @input="form.errors.clear(name)" />

        <!-- Country -->
        <Country v-else-if="type === 'country'" v-model="model" :placeholder="placeholder" :hasError="form.errors.has(name)" @input="form.errors.clear(name)" />

        <!-- Async Select -->
        <SelectAsync v-else-if="type === 'select-async'" v-model="model" :searchFunction="searchFunction" :placeholder="placeholder" :hasError="form.errors.has(name)" @input="form.errors.clear(name)">
            <template slot="option" slot-scope="props">
                <slot name="option" v-bind:option="props.option"></slot>
            </template>
            <template slot="singleLabel" slot-scope="props">
                <slot name="singleLabel" v-bind:option="props.option"></slot>
            </template>
        </SelectAsync>

        <!-- Tags -->
        <Tags v-else-if="type === 'tags'" v-model="model" :hasError="form.errors.has(name)" @input="form.errors.clear(name)" />

        <!-- Component -->
        <component v-else-if="type === 'component'" :is="component" v-model="model" :searchFunction="searchFunction" :placeholder="placeholder" :hasError="form.errors.has(name)" @input="form.errors.clear(name)" @remove="$emit('remove')" :multiple="multiple" />

        <!-- Error -->
        <Error v-if="form.errors.has(name)" :error="form.errors.get(name)" />

        <!-- Hint -->
        <InputHint v-if="hint">{{ hint }}</InputHint>

    </FormGroup>
</template>

<script>
    import moment from 'moment'
    import Input from '../Form/Input'
    import Textarea from '../Form/Textarea'
    import Editor from '../Form/Editor'
    import Radio from '../Form/Radio'
    import Checkbox from '../Form/Checkbox'
    import SelectAsync from '../Form/SelectAsync'
    import Select from '../Form/Select'
    import Country from '../Form/Country'
    import Error from '../Form/Error'
    import InputHint from '../Form/InputHint'
    import FormGroup from '../Form/FormGroup'
    import Tags from '../Form/Tags'
    import Users from '../Form/Models/Users'
    import BillingAccounts from '../Form/Models/BillingAccounts'
    import Wallets from "../Form/Models/Wallets";
    import Groups from '../Form/Models/Groups'
    import Partners from '../Form/Models/Partners'
    import Stores from '../Form/Models/Stores'
    import VoucherLists from '../Form/Models/VoucherLists'
    import Types from '../Form/Models/Types'
    import PhoneNumber from './PhoneNumber'
    let _ = require('lodash')

    export default {
        name: 'field',
        props: {
            form: {
                type: Object,
                required: true
            },
            name: {
                type: String,
                required: true
            },
            type: {
                default: 'text',
                validator: function (value) {
                    return ['text', 'password', 'number', 'date', 'datetime', 'phone', 'textarea', 'editor', 'radio', 'select-async', 'select', 'country', 'tags', 'component', 'checkbox', 'boolean'].includes(value)
                }
            },
            placeholder: String,
            hint: String,
            searchFunction: Function,
            component: {
                required: false,
                validator: function (value) {
                    return ['Users', 'Groups', 'Stores', 'VoucherLists', 'Types', 'Partners', 'BillingAccounts', 'Wallets'].includes(value)
                }
            },
            options: [ Array, Object ],
            optionsKeyBy: String,
            optionsLabel: String,
            translate: Boolean,
            multiple: Boolean,
            mask: String,
            availableLanguages: Array,
            displayLanguage: String,
            min: [ String, Number ],
            max: [ String, Number ],
            focus: Boolean
        },
        data() {
            return {
                lang: null
            }
        },
        components: {
            PhoneNumber,
            Input,
            Radio,
            Checkbox,
            Textarea,
            InputHint,
            SelectAsync,
            FormGroup,
            Error,
            Tags,
            Users,
            BillingAccounts,
            Stores,
            VoucherLists,
            Groups,
            Types,
            Editor,
            Partners,
            Select,
            Country,
            Wallets
        },
        methods: {
            setVal(val) {
                this.mainModel = val
                this.$emit('input', val)
            },
            inputModelText(value) {
                this.form.errors.clear(this.name)
                this.setVal(this.translate ? this.modelText : value)
            },
            textModelLangOptions() {
                return this.translate ? this.modelAvailableLanguages : [ this.modelDisplayLanguage ]
            },
            dynPlaceholder(lang) {
                return this.translate ? this.placeholder + ' - ' + lang : this.placeholder
            },
            setLang(lang) {
                this.lang = lang
                this.$emit('setLang', lang)
            }
        },
        computed: {
            mainModel: {
                get() {
                    return _.get(this.form, this.name)
                },
                set(val) {
                    _.set(this.form, this.name, val === '' ? null : val)
                }
            },
            model: {
                get() {
                    return this.mainModel
                },
                set(val) {
                    this.setVal(val)
                }
            },
            modelText: function() {
                let textOptions = this.textModelLangOptions()
                let result = {}
                textOptions.forEach(lang => {
                    let value = this.translate ? (this.model && this.model[lang] ? this.model[lang] : '') : this.model
                    result[lang] = value
                })
                return result
            },
            modelDate: {
                get() {
                    return moment(this.mainModel).format('YYYY-MM-DD')
                },
                set(val) {
                    val = moment(val).format('YYYY-MM-DD')
                    this.setVal(val)
                }
            },
            modelDateTime: {
                get() {
                    return moment(this.mainModel).format('YYYY-MM-DDTHH:mm:ss')
                },
                set(val) {
                    val = moment(val).format('YYYY-MM-DD HH:mm:ss')
                    this.setVal(val)
                }
            },
            radioOptions: function() {
                let result = []
                // Array
                if (Array.isArray(this.options)) {
                    for (let i = 0; i < this.options.length; i++) {
                        if (typeof this.options[i] === 'object' && this.optionsKeyBy && this.optionsLabel) {
                            result.push({
                                key: this.options[i][this.optionsKeyBy],
                                label: this.options[i][this.optionsLabel]
                            })
                        }
                        else {
                            result.push(this.options[i])
                        }
                    }
                }
                // Object
                else if (typeof this.options === 'object') {
                    for (let property in this.options) {
                        result.push({
                            key: property,
                            label: this.options[property]
                        })
                    }
                }
                return result
            },
            modelAvailableLanguages: function() {
                return this.availableLanguages ? this.availableLanguages : this.$config.config.availableLanguages
            },
            modelDisplayLanguage: function() {
                return this.displayLanguage ? this.displayLanguage : this.$config.config.displayLanguage
            }
        },
        watch: {
            'modelDisplayLanguage': {
                immediate: true,
                handler(val) {
                    this.lang = val
                }
            }
        }
    }
</script>
